import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchNewsletters } from '@/store/slices/newslettersSlice';
import { fetchActionItems } from '@/store/slices/actionItemsSlice';
import NewsletterRenderer from '@/components/newsletter/NewsletterRenderer';
import ExportMenu from '@/components/export/ExportMenu';
import { EmptyState, LoadingState } from '@/components/common/Common';
import { ROUTES } from '@/constants/routes';

export default function NewsletterPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: newsletters, status } = useAppSelector((state) => state.newsletters);
  const actionItems = useAppSelector((state) => state.actionItems.items);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchNewsletters());
    dispatch(fetchActionItems());
  }, [dispatch, status]);

  const newsletter = newsletters.find((n) => n.id === id);
  const newsletterActionItems = useMemo(
    () => actionItems.filter((item) => item.newsletterId === id),
    [actionItems, id],
  );

  if (status === 'loading' || status === 'idle') return <LoadingState label="Loading preview…" />;
  if (!newsletter) return <EmptyState title="Newsletter not found" description="It may have been deleted." />;

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" className="no-print" flexWrap="wrap" useFlexGap>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.newsletterList)}>
          Back to Newsletters
        </Button>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<EditOutlinedIcon />} onClick={() => navigate(ROUTES.newsletterEdit(newsletter.id))}>
            Edit
          </Button>
          <ExportMenu newsletter={newsletter} actionItems={newsletterActionItems} />
        </Stack>
      </Stack>
      <Typography variant="h2" className="no-print">
        Preview
      </Typography>
      <NewsletterRenderer newsletter={newsletter} actionItems={newsletterActionItems} />
    </Stack>
  );
}
